import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { useSchool } from '@/contexts/SchoolContext';
import { useRoleSwitcher } from '@/features/auth/hooks';
import { getParentCalendarEvents } from '@/services/calendar/calendarAudienceService';

/**
 * useCalendarEvents - Real-time calendar events for the current month.
 *
 * Staff see all events. Parents see only events scoped to their children's
 * grades/phases/classes (resolved via parentStore.linkedChildren).
 */
export function useCalendarEvents({ month, year, startDate = null, endDate = null }) {
  const { schoolId } = useSchool();
  const { currentRole } = useRoleSwitcher();

  const [startEvents, setStartEvents] = useState([]);
  const [spanEvents, setSpanEvents] = useState([]);
  const [parentEvents, setParentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const rangeStartMillis = startDate?.getTime?.() || null;
  const rangeEndMillis = endDate?.getTime?.() || null;
  const monthStart = useMemo(() => Timestamp.fromDate(
    rangeStartMillis == null
      ? startOfMonth(new Date(year, month))
      : startOfDay(new Date(rangeStartMillis))
  ), [year, month, rangeStartMillis]);
  const monthEnd = useMemo(() => Timestamp.fromDate(
    rangeEndMillis == null
      ? endOfMonth(new Date(year, month))
      : endOfDay(new Date(rangeEndMillis))
  ), [year, month, rangeEndMillis]);

  useEffect(() => {
    let active = true;
    if (currentRole !== 'parent' || !schoolId) {
      setParentEvents([]);
      return () => { active = false; };
    }
    setLoading(true);
    setError(null);
    getParentCalendarEvents({
      schoolId,
      startDate: monthStart.toDate(),
      endDate: monthEnd.toDate(),
    }).then((items) => {
      if (active) setParentEvents(items);
    }).catch((err) => {
      if (active) setError(err.message);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [currentRole, schoolId, monthStart, monthEnd, refreshKey]);

  // Listener 1: events starting in this month
  useEffect(() => {
    if (currentRole === 'parent') { setStartEvents([]); return; }
    if (!schoolId) { setStartEvents([]); setLoading(false); return; }
    setLoading(true);

    const q = query(
      collection(db, 'schools', schoolId, 'calendarEvents'),
      where('startDate', '>=', monthStart),
      where('startDate', '<=', monthEnd)
    );

    const unsub = onSnapshot(q, snap => {
      setStartEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error('[useCalendarEvents] startQ error:', err);
      setError(err.message);
      setLoading(false);
    });

    return unsub;
  }, [schoolId, currentRole, monthStart, monthEnd]);

  // Listener 2: events that started before this month (filter endDate client-side)
  useEffect(() => {
    if (currentRole === 'parent') { setSpanEvents([]); return; }
    if (!schoolId) { setSpanEvents([]); return; }

    const q = query(
      collection(db, 'schools', schoolId, 'calendarEvents'),
      where('startDate', '<', monthStart)
    );

    const unsub = onSnapshot(q, snap => {
      const filtered = snap.docs
        .filter(d => {
          const endDate = d.data().endDate;
          return endDate && endDate.toMillis() >= monthStart.toMillis();
        })
        .map(d => ({ id: d.id, ...d.data() }));
      setSpanEvents(filtered);
    }, err => {
      console.error('[useCalendarEvents] spanQ error:', err);
      setError(err.message);
    });

    return unsub;
  }, [schoolId, currentRole, monthStart]);

  // Merge, dedupe, sort, and apply audience filtering
  const events = useMemo(() => {
    if (currentRole === 'parent') {
      return [...parentEvents].sort((a, b) => {
        const aTime = a.startDate?.toMillis?.() || 0;
        const bTime = b.startDate?.toMillis?.() || 0;
        return aTime - bTime;
      });
    }
    const eventMap = new Map();
    for (const e of startEvents) eventMap.set(e.id, e);
    for (const e of spanEvents) if (!eventMap.has(e.id)) eventMap.set(e.id, e);

    return [...eventMap.values()].sort((a, b) => {
      const aTime = a.startDate?.toMillis?.() || 0;
      const bTime = b.startDate?.toMillis?.() || 0;
      return aTime - bTime;
    });
  }, [startEvents, spanEvents, currentRole, parentEvents]);

  return { events, loading, error, refetch: () => setRefreshKey((value) => value + 1) };
}

export default useCalendarEvents;
